import type { themeColors } from '@ianesthesie-kit/config';
import type { FollowUpPlan } from '@/modules/followUpPlan';
import type { Invoice } from './billing.model';
import { findTargetPlan } from './invoice.utils';

/** A quote's state, as shown on its status badges. */
export type QuoteState =
  | 'done'
  | 'signedOnSite'
  | 'printed'
  | 'notSigned'
  | 'notSent';

export const quoteStateOf = (invoice: Invoice): QuoteState => {
  if (invoice.status === 'SIGNED_ON_SITE') {
    return 'signedOnSite';
  }
  if (invoice.status === 'SIGNED') {
    return 'done';
  }
  if (invoice.status === 'PRINTED') {
    return 'printed';
  }
  if (invoice.status === 'SENT') {
    return 'notSigned';
  }
  // Fallback for invoices without status field
  if (invoice.signedByPatient) {
    return 'done';
  }
  if (invoice.sent) {
    return 'notSigned';
  }
  return 'notSent';
};

/** The theme palette each quote state is shown in. */
export const QUOTE_STATE_PALETTE = {
  done: 'green',
  signedOnSite: 'green',
  notSigned: 'yellow',
  printed: 'blue',
  notSent: 'red'
} as const satisfies Record<QuoteState, keyof typeof themeColors>;

/** The i18n key of each quote state's label. */
export const QUOTE_STATE_LABEL_KEYS: Record<QuoteState, string> = {
  done: 'doctor-dashboard.details.billingCard.quoteState.done',
  signedOnSite: 'doctor-dashboard.details.billingCard.quoteState.signedOnSite',
  notSigned: 'doctor-dashboard.details.billingCard.quoteState.notSigned',
  printed: 'doctor-dashboard.details.billingCard.quoteState.printed',
  notSent: 'doctor-dashboard.details.billingCard.quoteState.notSent'
};

/**
 * What a quote's header chip reads: a date, alone or framed by a label. The bare date is the
 * issue date, so the chip gives the issue date in a tooltip only when there is a label.
 */
export type QuoteChipLabel = {
  /** The i18n key that frames the date as `{date}`; without one the chip shows the date alone. */
  labelKey?: string;
  date: string;
};

/**
 * The label of a quote's header chip. Without an open plan the chip shows the issue date, as it
 * always did; with open plans it says which surgery the quote is for, dated by that surgery.
 */
export const quoteChipLabel = (
  invoice: Invoice,
  openPlans: FollowUpPlan[] | undefined,
  eventSurgeryDate: string | undefined
): QuoteChipLabel => {
  const planId = invoice.followUpPlanId;
  if (!planId) {
    if (!openPlans?.length) {
      return { date: invoice.issuedAt };
    }
    return {
      labelKey: 'doctor-dashboard.details.billingCard.chip.event',
      date: eventSurgeryDate ?? invoice.issuedAt
    };
  }
  const plan = findTargetPlan(openPlans, { kind: 'plan', planId });
  // A created plan's quote has its plan id cleared, so once the plans are loaded, a plan quote
  // whose plan is not open belongs to a cancelled plan
  if (openPlans && !plan) {
    return {
      labelKey: 'doctor-dashboard.details.billingCard.chip.plannedCancelled',
      date: invoice.issuedAt
    };
  }
  return {
    labelKey: 'doctor-dashboard.details.billingCard.chip.planned',
    date: plan?.payload.surgeryDate ?? invoice.issuedAt
  };
};
